import { useState } from "react";

import { Grid, Hidden } from "@material-ui/core";

import { cancelTransfer } from "api/transfer";

import { ConfirmDialog, ProductTypeFilter } from "components/Custom";
import { Button, DatePickerGroup, Dropdown } from "components/Form";
import { FormattedTextField } from "components/FormElements";

import { popupNotification, useLayoutDispatch } from "context/LayoutContext";
import { useUserState } from "context/UserContext";

import { Class, QrCode2, Share, SpaceBar, X } from "icons";

import { PUBLISH_TYPES } from "utils/constants";

import TransferPublishModal from "./PublishModal";

export default function TransferFilter({
  handleChangeFilterInput,
  params,
  clearAdornment,
  debouncedSearchHandler,
  setParams,
  selectedTransfers = [],
  headers = [],
  afterSuccessPublish,
}) {
  const layoutDispatch = useLayoutDispatch();
  const [publish, setPublish] = useState(false);
  const [cancel, setCancel] = useState(false);
  const { permissions } = useUserState();
  const hasPublishPermission = permissions?.includes(
    "contenttypes.transfer.publish",
  );
  const hasCancelPermission = permissions?.includes("warehouse.transfer.cancel");

  const handleClosePublishModal = (status) => {
    if (status) {
      afterSuccessPublish(status);
    }
    setPublish(false);
  };

  const handleTransferCancel = () => {
    cancelTransfer({
      body: {
        ids: selectedTransfers.map((s) => s.id),
      },
      responseSetter: (res) => {
        const { success, message } = res;
        if (!success) {
          return popupNotification({
            dispatch: layoutDispatch,
            message,
          });
        }

        afterSuccessPublish();

        return popupNotification({
          dispatch: layoutDispatch,
          message,
          status: "success",
        });
      },
    });
  };

  return (
    <Grid container>
      <DatePickerGroup
        startDateValue={params.start_date}
        startDateOnChange={handleChangeFilterInput("start_date")}
        endDateValue={params.end_date}
        endDateOnChange={handleChangeFilterInput("end_date")}
        itemGridProps={{ xs: 12, sm: 6, md: 4, lg: 2, xl: 2 }}
      />
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <FormattedTextField
          fullWidth
          id="sku"
          label="SKU"
          variant="outlined"
          value={params.product__sku}
          onChange={handleChangeFilterInput("product__sku")}
          InputProps={clearAdornment("product__sku")}
          LeftIcon={QrCode2}
          isProductSKU={true}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <FormattedTextField
          fullWidth
          id="from_location"
          label="From Location"
          variant="outlined"
          value={params.from_location__code}
          onChange={handleChangeFilterInput("from_location__code")}
          InputProps={clearAdornment("from_location__code")}
          LeftIcon={SpaceBar}
          isProductSKU={true}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <FormattedTextField
          fullWidth
          id="to_location"
          label="To Location"
          variant="outlined"
          value={params.to_location__code}
          onChange={handleChangeFilterInput("to_location__code")}
          InputProps={clearAdornment("to_location__code")}
          LeftIcon={SpaceBar}
          isProductSKU={true}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <Dropdown
          id="is_published"
          title="Is Published"
          value={params.is_published}
          onChange={handleChangeFilterInput("is_published")}
          items={[
            {
              value: true,
              label: "Published",
            },
            {
              value: false,
              label: "Not Published",
            },
          ]}
          LeftIcon={Class}
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
        md={4}
        lg={2}
        style={{
          border: !params.type ? "1px solid #ff5555" : "inherit",
        }}
      >
        <Dropdown
          id="type"
          title="Transfer Type"
          value={params.type}
          onChange={handleChangeFilterInput("type")}
          items={[
            {
              value: "shipment",
              label: "Shipment",
            },
            ...PUBLISH_TYPES,
          ]}
          LeftIcon={Class}
        />
      </Grid>
      <ProductTypeFilter
        value={params.product__product_type}
        setValue={(data) => {
          setParams({ ...params, product__product_type: data });
          debouncedSearchHandler();
        }}
        onChange={handleChangeFilterInput("product__product_type")}
      />

      <Hidden only={["md", "xs"]}>
        <Grid
          item
          lg={hasCancelPermission && hasPublishPermission ? 4 : 6}
          sm={6}
        />
      </Hidden>
      {hasCancelPermission ? (
        <Grid item lg={2} md={4} sm={6} xs={12}>
          <Button
            fullWidth
            variant="contained"
            size="medium"
            color="danger"
            disabled={!!params.is_published || !selectedTransfers.length}
            onClick={() => setCancel(true)}
          >
            <X color="white" />
            Cancel
          </Button>
        </Grid>
      ) : null}
      {hasPublishPermission ? (
        <Grid item lg={2} md={4} sm={6} xs={12}>
          <Button
            fullWidth
            variant="contained"
            size="medium"
            color="blue"
            disabled={
              !!params.is_published || !params.type || !selectedTransfers.length
            }
            onClick={() => setPublish(true)}
          >
            <Share color="white" />
            Publish
          </Button>
        </Grid>
      ) : null}
      {publish && (
        <TransferPublishModal
          onClose={handleClosePublishModal}
          transfers={selectedTransfers}
          headers={headers}
        />
      )}
      <ConfirmDialog
        open={cancel}
        title="Cancel Transfers"
        children={`${selectedTransfers.length} transfer records will be cancelled`}
        onClose={() => setCancel(false)}
        onConfirm={handleTransferCancel}
      />
    </Grid>
  );
}
